import { useEffect, useState } from "react";
import { AppShell, Header } from "../layout";
import { DateSelector } from "../components/DateSelector";
import type { Role } from "@ub/shared";
import css from "./PreviewScreen.module.css";

/**
 * Stage 1b smoke-test page.
 *
 * Lets you flip through all five roles and see Header + AppShell with the
 * correct role-coloured wordmark + HUD plaque. The DateSelector slot is a
 * placeholder until Stage 1c lands the real M.1 trigger. This screen will
 * be removed once Stage 1d wires the real auth + role-switcher per B.1.
 */

const ROLE_OPTIONS: { id: Role; label: string; dataRole: string }[] = [
  { id: "DC", label: "DEP. OF COMMERCE", dataRole: "dc" },
  { id: "OO", label: "OFFICE OPERATIONS", dataRole: "oo" },
  { id: "EO", label: "EXECUTIVE OFFICE", dataRole: "eo" },
  { id: "OP", label: "OPERATOR", dataRole: "op" },
];

export function PreviewScreen() {
  const [active, setActive] = useState<Role>("OO");
  const activeOption = ROLE_OPTIONS.find((r) => r.id === active) ?? ROLE_OPTIONS[1]!;

  useEffect(() => {
    document.documentElement.dataset.role = activeOption.dataRole;
  }, [activeOption]);

  return (
    <AppShell
      header={
        <Header
          userName="kartman"
          roleLabel={activeOption.label}
          dateSelectorSlot={<DateSelector mode="day" />}
        />
      }
    >
      <section className={css.body}>
        <p className={css.kicker}>STAGE 1B · LAYOUT PREVIEW</p>
        <h2 className={css.h}>Role-coloured Header + AppShell</h2>
        <p className={css.lead}>
          Жмёшь кнопку — меняется <code>data-role</code> на &lt;html&gt;, всё что использует
          <code> var(--role)</code> перекрашивается. Шапка наследует акцент, рамки и текст HUD
          плашки автоматически.
        </p>
        <div className={css.roleRow} role="radiogroup" aria-label="Текущая роль">
          {ROLE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active === opt.id}
              className={`${css.roleBtn} ${active === opt.id ? css.roleBtnOn : ""}`}
              onClick={() => setActive(opt.id)}
            >
              {opt.id}
            </button>
          ))}
        </div>

        <div className={css.legend}>
          <div className={css.swatch}>
            <span className={css.dot} style={{ background: "var(--role)" }} />
            <span>--role</span>
            <code className={css.code}>{getCss("--role")}</code>
          </div>
          <p className={css.muted}>
            Real DateSelector (M.1) подъедет в Stage&nbsp;1c — пока в правом слоте Header
            заглушка.
          </p>
        </div>
      </section>
    </AppShell>
  );
}

/** Read a CSS custom property from :root after layout. Best-effort for the legend swatch. */
function getCss(name: string) {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
